import merge from "lodash.merge";
import {
	BorderWidths,
	Breakpoints,
	Fonts,
	FontWeights,
	Radii,
	Shadows,
	Space,
} from "../../types/theming";
import { GRID_BASELINE } from "../../components/layout/Grid/Grid.types";
import { Unit } from "../../types/globals";

/**
 * BorderWidthAliasAttributeLegacy
 * #Legacy
 */
type BorderWidthAliasAttributeLegacy =
	| "border-size-thin"
	| "border-size-thick"
	| "border-size-bold";

/**
 * BorderWidthAliasLegacy
 * #Legacy
 */
export type BorderWidthAliasLegacy = {
	[key in BorderWidthAliasAttributeLegacy]?: string;
};

/**
 * borderWidths
 * #Legacy: The string[] and BorderWidthAliasLegacy and corresponding array values are here to satisfy legacy requirements.
 */
const borderWidthValues: BorderWidths = {
	sm: "1px",
	md: "2px",
	lg: "6px",
};

export const borderWidths: string[] & BorderWidthAliasLegacy & BorderWidths =
	merge(["1px", "2px", "6px"], borderWidthValues);

/**
 * BreakpointAliasAttributeLegacy
 * #Legacy
 */
type BreakpointAliasAttributeLegacy =
	| "mobile"
	| "tablet"
	| "desktop"
	| "header";

/**
 * BreakpointAliasLegacy
 * #Legacy
 */
export type BreakpointAliasLegacy = {
	[key in BreakpointAliasAttributeLegacy]: string;
};

/**
 * breakpoints
 * #Legacy: The string[] and BreakpointAliasLegacy and corresponding array values are here to satisfy legacy requirements.
 */
const breakpointValues: Breakpoints = {
	xs: 0,
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1200,
	xxl: 1440,
};

export const breakpoints: string[] & BreakpointAliasLegacy & Breakpoints =
	merge(["640px", "1024px", "1200px"], breakpointValues, {
		mobile: "0px",
		tablet: "415px",
		desktop: "769px",
		header: "760px",
	});

/**
 * fonts
 * #Legacy: The normal prop in the root of this object is there for legacy support.
 */
export const fonts: Fonts = {
	normal: "Roboto, sans-serif", // #Legacy
	body: "Roboto, sans-serif",
};

/**
 * FontSizeAliasAttributeLegacy
 * #Legacy
 */
type FontSizeAliasAttributeLegacy =
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "body"
	| "body-small"
	| "body-medium"
	| "body-large"
	| "body-lg"
	| "label"
	| "caption";

/**
 * FontSizeAliasLegacy
 * #Legacy
 */
export type FontSizeAliasLegacy = {
	[key in FontSizeAliasAttributeLegacy]?: number;
};

/**
 * fontSizes
 * #Legacy: The number[] and FontSizeAliasLegacy and corresponding array values are here to satisfy legacy requirements.
 */
export const fontSizes: number[] & FontSizeAliasLegacy = [
	12, 14, 16, 18, 20, 24, 28, 32, 36,
];

/**
 * FontWeightAliasAttributeLegacy
 * #Legacy
 */
type FontWeightAliasAttributeLegacy =
	| "light"
	| "normal"
	| "semiBold"
	| "medium"
	| "bold";

/**
 * FontWeightAliasLegacy
 * #Legacy
 */
export type FontWeightAliasLegacy = {
	[key in FontWeightAliasAttributeLegacy]?: number;
};

/**
 * fontWeights
 * #Legacy: The number[] and FontWeightAliasLegacy and corresponding array values are here to satisfy legacy requirements.
 */
const fontWeightValues: FontWeights = {
	values: {
		lightest: 400,
		light: 400,
		regular: 400,
		heavy: 700,
		heaviest: 700,
	},
};

export const fontWeights: number[] & FontWeightAliasLegacy & FontWeights =
	merge([300, 400, 500, 700], fontWeightValues);

/**
 * opacity
 * #Legacy: ?? Haven't spoken with the designers about this one yet ??
 */
export const opacity: number[] = [0, 0.25, 0.5, 0.75, 1];

/**
 * ShadowAliasAttributeLegacy
 * #Legacy
 */
type ShadowAliasAttributeLegacy =
	| "default"
	| "hover"
	| "accent"
	| "error"
	| "warning";

/**
 * ShadowAliasLegacy
 * #Legacy
 */
export type ShadowAliasLegacy = {
	[key in ShadowAliasAttributeLegacy]?: string;
};

/**
 * shadows
 * #Legacy: ?? Haven't spoken with the designers about this one yet ??
 */
const shadowValues: Shadows = {
	shadow_100: "0px 12px 24px rgba(0, 0, 0, 0.1)",
	shadow_200: "0px 12px 24px rgba(0, 0, 0, 0.2)",
};
export const shadows: string[] & ShadowAliasLegacy & Shadows = merge(
	[
		"0 10px 10px 0 rgba(219, 219, 219, 0.5)",
		"0 2px 9px 0 rgba(15, 156, 246, 0.5)",
		"0 3px 25px 0 rgba(218, 218, 218, 0.37)",
		"0 3px 26px 0 rgba(36, 160, 244, 0.47)",
		"0 0 9px 0 rgba(227, 6, 19, 0.5)",
		"0px 3px 25px rgba(103, 103, 103, 0.37)",
		"0 0 9px 0 rgba(238, 149, 4, 0.5)",
	],
	shadowValues
);

/**
 * SpaceAliasAttributeLegacy
 * #Legacy
 */
type SpaceAliasAttributeLegacy = "mobileGridMargin" | "desktopGridMargin";

/**
 * SpaceAliasLegacy
 * #Legacy
 */
export type SpaceAliasLegacy = {
	[key in SpaceAliasAttributeLegacy]?: string;
};

/**
 * space
 * Note: The new values have not yet been confirmed, so consider these "placeholder values" for now.
 * #Legacy: The number[] and corresponding array values are here to satisfy legacy requirements.
 */

/**
 * The SPACE_BASELINE const is only exported for the rare cases when a theme is unavailable. If you
 * can access a theme object, then access the 'baseline' this way... theme.space.baseline.
 */
const spaceValues: Space = {
	xxs: `${1 * GRID_BASELINE}px` as Unit,
	xs: `${2 * GRID_BASELINE}px` as Unit,
	sm: `${3 * GRID_BASELINE}px` as Unit,
	md: `${4 * GRID_BASELINE}px` as Unit,
	lg: `${6 * GRID_BASELINE}px` as Unit,
	xl: `${8 * GRID_BASELINE}px` as Unit,
	xxl: `${12 * GRID_BASELINE}px` as Unit,
	xxxl: `${16 * GRID_BASELINE}px` as Unit,
	"xxxl-2": `${24 * GRID_BASELINE}px` as Unit,
	"xxxl-3": `${32 * GRID_BASELINE}px` as Unit,
	"xxxl-4": `${48 * GRID_BASELINE}px` as Unit,
	"xxxl-5": `${64 * GRID_BASELINE}px` as Unit,
};

export const space: number[] & SpaceAliasLegacy & Space = merge(
	[0, 4, 8, 12, 16, 20, 24, 28, 32, 36],
	spaceValues
);

/**
 * radii
 * #Legacy: The number[] and corresponding array values are here to satisfy legacy requirements.
 */
const radiiValues: Radii = spaceValues;
export const radii: number[] & Radii = merge([0, 4, 8], radiiValues);
